// Port of VideoDisplay() in android/.../ui/MainActivity.kt (ExoPlayer-based).
// HTML5 <video> handles caching + buffering via the browser/Service Worker;
// we just orchestrate autoplay, error retry, and "play to full natural length."
//
// Autoplay rules: muted+inline+autoplay is allowed everywhere without a user
// gesture (Chrome, Safari, Edge, Firefox), so this works in a fresh-loaded tab.
// loop=true on REPEAT_MODE_ONE parity with Android — short clips loop until
// the duration timer fires instead of holding on the last frame.

import { useEffect, useRef, useState } from 'react'
import { absoluteUrl } from '../lib/contentUrl'
import ContentErrorScreen from './ContentErrorScreen'

interface Props {
  url:            string
  durationMs:     number
  audioUnmuted?:  boolean        // from Screen.display_settings.audio_unmuted
  videoMuted?:    boolean        // screen-level mute for video audio (display_settings.video_muted)
  volume?:        number         // master audio level 0–100 (display_settings.volume)
  itemMuted?:     boolean        // per-row mute from the playlist (playlist_media.muted)
  audioUnlocked?: boolean        // user gesture received → unmuted playback is legal
  scaling?:       'default' | 'fit'  // 'default'=contain (letterbox); 'fit'=cover (fill)
  // Covered by a stop-screen overlay → freeze this video (pause the element +
  // hold the rotation timer) so it isn't decoding under an opaque overlay.
  // Resumes where it left off on reveal — the element stays mounted.
  paused?:        boolean
  onFinished:     () => void
  onError:        (msg: string) => void
  onRetry:        (url: string) => void
}

export default function VideoDisplay({
  url, durationMs, audioUnmuted = false, videoMuted = false, volume = 100, itemMuted = false, audioUnlocked = false, scaling = 'default',
  paused = false,
  onFinished, onError, onRetry,
}: Props) {
  const fullUrl = absoluteUrl(url)
  // A video carries sound only when the screen is unmuted AND video audio isn't
  // muted screen-wide AND this row wasn't muted in the playlist editor. Any one
  // mutes it. We also force-mute until the page has received a user gesture —
  // browsers block non-muted autoplay otherwise and the video would just stall
  // on a paused first frame.
  const muted = !audioUnmuted || videoMuted || itemMuted || !audioUnlocked
  // Master level 0–1 applied to the element when it's unmuted.
  const level = Math.max(0, Math.min(100, volume)) / 100
  const videoRef       = useRef<HTMLVideoElement | null>(null)
  const retriedRef     = useRef(false)
  const finishedRef    = useRef(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [actualMs,  setActualMs]  = useState(0)
  const [hasError,  setHasError]  = useState(false)

  // Apply mute changes imperatively to the live element so flipping between
  // unmuted/muted (e.g. when the user finally taps the page) takes effect
  // without remounting the <video> and restarting playback. After unmuting
  // we re-issue play() — Chrome treats play() as the canonical
  // "now I want audio" signal, and re-calling it ensures the audio track
  // actually starts even if the previous autoplay ran while muted.
  useEffect(() => {
    const v = videoRef.current
    if (!v) return
    if (v.muted !== muted) {
      try { v.muted = muted } catch { /* ignore */ }
    }
    // Apply the screen's master level to the live element (0–1). Set it even
    // while muted so unmuting later plays at the chosen level immediately.
    try { v.volume = level } catch { /* ignore */ }
    if (!muted) {
      try {
        const p = v.play()
        if (p && typeof p.then === 'function') {
          p.catch(() => { /* gesture missing — onPause fallback will mute */ })
        }
      } catch { /* ignore */ }
    }
  }, [muted, level])

  const advance = () => {
    if (finishedRef.current) return
    finishedRef.current = true
    onFinished()
  }

  // Pause/resume the element imperatively when the overlay covers/reveals this
  // video, without remounting (which would reload + restart it).
  useEffect(() => {
    const v = videoRef.current
    if (!v) return
    if (paused) {
      try { v.pause() } catch { /* ignore */ }
    } else if (v.paused && isPlaying) {
      const p = v.play()
      if (p && typeof p.then === 'function') p.catch(() => { /* gesture missing */ })
    }
  }, [paused, isPlaying])

  // Rotation timer: starts when playback begins, runs for the video's
  // *real* duration so short clips play in full instead of being cut off
  // at the configured `durationMs` placeholder. Frozen while `paused` (covered
  // by an overlay) so a hidden video doesn't advance the rotation.
  useEffect(() => {
    if (!isPlaying || paused) return
    const waitMs = actualMs > 0 ? actualMs : durationMs
    const t = window.setTimeout(advance, waitMs)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, actualMs, durationMs, paused])

  // Safety valve: durationMs + 60s.
  useEffect(() => {
    const t = window.setTimeout(advance, durationMs + 60_000)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullUrl, durationMs])

  // Load timeout — if the video never reaches `canplay`/`playing` within 10s
  // (Firefox failing silently on an unsupported codec, S3 stalling, network
  // hiccup mid-publish), treat the slot as finished so the carousel keeps
  // moving instead of sitting on a black overlay until the 60s safety valve
  // fires. The error screen still renders if `onError` lands later.
  useEffect(() => {
    if (isPlaying) return
    const t = window.setTimeout(() => {
      if (!isPlaying) {
        setIsPlaying(true)
        advance()
      }
    }, 10_000)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, fullUrl])

  if (hasError) return <ContentErrorScreen />

  return (
    <div className="absolute inset-0 bg-black">
      <video
        ref={videoRef}
        src={fullUrl}
        // CORS mode, same as ImageDisplay: without it the request is no-cors,
        // the Service Worker caches an OPAQUE response, and its range-request
        // handling cannot slice an opaque body — so a video played once from
        // the network and then failed with "Format error" on every replay
        // from cache (every loop, every rotation). The CDN sends
        // Access-Control-Allow-Origin: *.
        crossOrigin="anonymous"
        autoPlay
        muted={muted}
        loop
        playsInline
        preload="auto"
        className={`w-full h-full ${scaling === 'fit' ? 'object-cover' : 'object-contain'}`}
        style={{ opacity: isPlaying ? 1 : 0, transition: 'opacity 240ms ease-out' }}
        onLoadedData={(e) => {
          const v = e.currentTarget
          if (v.duration && Number.isFinite(v.duration) && actualMs === 0) {
            setActualMs(Math.round(v.duration * 1000))
          }
        }}
        // Firefox sometimes lands on `canplay` without ever firing `playing`,
        // leaving the black flash-suppression overlay up forever. Treat
        // either as "we can show pixels now" so the overlay clears.
        onCanPlay={(e) => {
          setIsPlaying(true)
          if (e.currentTarget.paused) {
            void e.currentTarget.play().catch(() => { /* autoplay handled in onPause */ })
          }
        }}
        onPlaying={() => setIsPlaying(true)}
        onError={() => {
          if (!retriedRef.current) {
            retriedRef.current = true
            onRetry(fullUrl)
            const v = videoRef.current
            if (v) {
              try { v.load() } catch { /* ignore */ }
            }
          } else {
            setHasError(true)
            setIsPlaying(true)            // advance even on error
            onError(`Video error: ${fullUrl}`)
          }
        }}
        onPause={(e) => {
          // Skip when WE caused this pause (the `paused` prop, set while a
          // stop-screen overlay covers this video) — otherwise this handler
          // immediately calls play() again right after our own pause(),
          // fighting it every render and leaving the video decoding under
          // an opaque overlay (defeats the whole point of pausing it). The
          // pending play() from that fight is also a prime source of
          // Chrome's "AbortError: play() interrupted by a new load
          // request" — pause()/play() colliding mid-flight, tightened by
          // load() retries on the same element.
          if (paused) return
          // Browsers may block autoplay with sound unless the tab already
          // had a user gesture. If unmuted autoplay was rejected we get
          // paused immediately — fall back to muted so the carousel
          // doesn't stall on a paused first frame. The operator can flip
          // the setting back off and physically tap the screen later to
          // unlock audio on this surface (FullscreenPrompt handles that).
          if (!muted && e.currentTarget.paused && !e.currentTarget.ended) {
            try {
              e.currentTarget.muted = true
              e.currentTarget.play().catch(() => { /* ignore — e.g. interrupted by a load() retry */ })
            } catch { /* ignore */ }
          }
        }}
      />
      {/* Black overlay until playing — same flash-suppression as Android's
          ExoPlayer shutter + AnimatedVisibility(exit = fadeOut) combo. */}
      {!isPlaying && <div className="absolute inset-0 bg-black" />}
    </div>
  )
}
